import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

function getScrollOffset() {
  return {
    x:
      window.pageXOffset !== undefined
        ? window.pageXOffset
        : (
            document.documentElement ||
            document.body.parentNode ||
            document.body
          ).scrollLeft,
    y:
      window.pageYOffset !== undefined
        ? window.pageYOffset
        : (
            document.documentElement ||
            document.body.parentNode ||
            document.body
          ).scrollTop,
  };
}

function Autocomplete(props) {
  const {
    items,
    value = "",
    onChange = () => {},
    onSelect = () => {},
    valueProperty = "value",
    labelProperty = "label",
    isLoading,
    placeholder = "",
    shouldItemRender,
    isItemSelectable = () => true,
    sortItems,
    renderItem = (item, isHighlighted) =>
      item[valueProperty] && (
        <div
          style={
            isHighlighted
              ? { ...itemStyles, ...highlightedItemStyles }
              : { ...itemStyles }
          }
          key={item[valueProperty]}
        >
          {item[labelProperty]}
        </div>
      ),
    renderMenu = (items, value) => (
      <div style={{ ...menuStyles }}>
        {value === "" ? (
          <div
            style={{
              color: "#717273",
              display: "flex",
              justifyContent: "center",
            }}
          >
            No Options
          </div>
        ) : isLoading ? (
          <div
            style={{
              color: "#717273",
              display: "flex",
              justifyContent: "center",
            }}
          >
            Loading...
          </div>
        ) : items.length === 0 ? (
          <div
            style={{
              color: "#717273",
              display: "flex",
              justifyContent: "center",
            }}
          >
            No Options
          </div>
        ) : (
          items
        )}
      </div>
    ),
    menuStyle = defaultMenuStyle,
    renderInput = (props) => <input {...props} />,
    inputProps = {},
    wrapperProps = {},
    wrapperStyle = wrapperStyles,
    autoHighlight = true,
    selectOnBlur = false,
    onMenuVisibilityChange = () => {},
    open: controlledOpen,
    isEditing = true,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef({});
  const ignoreBlur = useRef(false);
  const ignoreFocus = useRef(false);
  const scrollOffset = useRef(null);
  const scrollTimer = useRef(null);

  const actualIsOpen = controlledOpen !== undefined ? controlledOpen : isOpen;

  useEffect(() => {
    if (items.length >= 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(null);
    }
  }, [items]);

  const getItemValue = (item) => {
    if (!item) {
      return "";
    }

    if (item[valueProperty]) {
      return item[valueProperty];
    }

    return "";
  };

  const getFilteredItems = useCallback(() => {
    let filtered = [...items];
    if (shouldItemRender) {
      filtered = filtered.filter((item) => shouldItemRender(item, value));
    }
    if (sortItems) {
      filtered.sort((a, b) => sortItems(a, b, value));
    }
    return filtered;
  }, [items, value, shouldItemRender, sortItems]);

  const setMenuPositions = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(inputRef.current);
      const marginBottom = parseInt(computedStyle.marginBottom, 10) || 0;
      const marginLeft = parseInt(computedStyle.marginLeft, 10) || 0;
      setMenuPos({
        top: rect.bottom + marginBottom,
        left: rect.left + marginLeft,
      });
    }
  }, []);

  useEffect(() => {
    if (actualIsOpen) {
      setMenuPositions();
    }
  }, [actualIsOpen, setMenuPositions]);

  useEffect(() => {
    return () => {
      clearTimeout(scrollTimer.current);
    };
  }, []);

  useEffect(() => {
    if (autoHighlight && (highlightedIndex === null || value)) {
      const items = getFilteredItems();
      let index = 0;
      for (let i = 0; i < items.length; i++) {
        if (isItemSelectable(items[index])) break;
        index = (index + 1) % items.length;
      }
      const matchedItem =
        items[index] && isItemSelectable(items[index]) ? items[index] : null;
      if (value !== "" && matchedItem) {
        const itemValue = getItemValue(matchedItem).toString();
        if (itemValue.toLowerCase().indexOf(value.toLowerCase()) === 0) {
          setHighlightedIndex(index);
        }
      }
    }
  }, [value, autoHighlight, getFilteredItems]);

  const handleInputChange = (e) => {
    onChange(e, e.target.value);
  };

  const handleInputFocus = (e) => {
    if (ignoreFocus.current) {
      ignoreFocus.current = false;
      const { x, y } = scrollOffset.current;
      window.scrollTo(x, y);
      scrollTimer.current = setTimeout(() => window.scrollTo(x, y), 0);
      return;
    }
    setIsOpen(true);
    inputProps.onFocus && inputProps.onFocus(e);
  };

  const handleInputBlur = (e) => {
    if (ignoreBlur.current) {
      console.log("test");
      ignoreFocus.current = true;
      scrollOffset.current = getScrollOffset();
      inputRef.current.focus();
      return;
    }

    let callback;
    if (selectOnBlur && highlightedIndex !== null) {
      const items = getFilteredItems();
      const item = items[highlightedIndex];
      const newValue = getItemValue(item);
      callback = () => onSelect(newValue, item);
    }
    setIsOpen(false);
    setHighlightedIndex(null);
    callback && callback();
    inputProps.onBlur && inputProps.onBlur(e);
  };

  const handleKeyDown = (e) => {
    const items = getFilteredItems();
    if (!items.length) return;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        let index = highlightedIndex === null ? -1 : highlightedIndex;
        for (let i = 0; i < items.length; i++) {
          const p = (index + i + 1) % items.length;
          if (isItemSelectable(items[p])) {
            setHighlightedIndex(p);
            setIsOpen(true);
            break;
          }
        }
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        let index = highlightedIndex === null ? items.length : highlightedIndex;
        for (let i = 0; i < items.length; i++) {
          const p = (index - (1 + i) + items.length) % items.length;
          if (isItemSelectable(items[p])) {
            setHighlightedIndex(p);
            setIsOpen(true);
            break;
          }
        }
        break;
      }
      case "Enter": {
        if (!actualIsOpen || highlightedIndex === null) return;
        e.preventDefault();
        const item = items[highlightedIndex];
        const newValue = getItemValue(item);
        setIsOpen(false);
        setHighlightedIndex(null);
        inputRef.current.setSelectionRange(newValue.length, newValue.length);
        onSelect(newValue, item);
        break;
      }
      case "Escape": {
        setIsOpen(false);
        setHighlightedIndex(null);
        break;
      }
      case "Tab": {
        ignoreBlur.current = false;
        handleTabClick();
        break;
      }
      default:
        if (!actualIsOpen) setIsOpen(true);
        break;
    }
  };

  const handleTabClick = () => {
    if (!isOpen) {
      return;
    }

    let callback;
    const items = getFilteredItems();

    if (highlightedIndex !== null) {
      const item = items[highlightedIndex];
      const newValue = getItemValue(item);
      callback = () => onSelect(newValue, item);
    } else if (items.length >= 1) {
      const item = items[0];
      const newValue = getItemValue(item);
      callback = () => onSelect(newValue, item);
    }

    setIsOpen(false);
    setHighlightedIndex(null);
    callback && callback();
  };

  const handleItemClick = (item) => {
    const newValue = getItemValue(item);
    ignoreBlur.current = false;
    setIsOpen(false);
    setHighlightedIndex(null);
    onSelect(newValue, item);
  };

  const renderMenuItems = () => {
    return getFilteredItems().map((item, index) => {
      const element = renderItem(item, highlightedIndex === index, {
        cursor: "default",
      });
      return React.cloneElement(element, {
        key: index,
        ref: (el) => (itemRefs.current[`item-${index}`] = el),
        onMouseEnter: isItemSelectable(item)
          ? () => setHighlightedIndex(index)
          : null,
        onClick: isItemSelectable(item) ? () => handleItemClick(item) : null,
      });
    });
  };

  return (
    <div style={{ ...wrapperStyle }} {...wrapperProps}>
      {renderInput({
        ...inputProps,
        style: {
          ...inputStyles,
          backgroundColor: isEditing ? "" : "#eee",
        },
        placeholder: placeholder,
        role: "combobox",
        "aria-autocomplete": "list",
        "aria-expanded": actualIsOpen,
        autoComplete: "off",
        ref: inputRef,
        onFocus: handleInputFocus,
        onBlur: handleInputBlur,
        onChange: handleInputChange,
        onKeyDown: handleKeyDown,
        disabled: !isEditing,
        value,
      })}
      <ArrowSVG />
      {actualIsOpen &&
        React.cloneElement(
          renderMenu(renderMenuItems(), value, {
            top: menuPos.top,
            left: menuPos.left,
            position: "fixed",
          }),
          {
            ref: menuRef,
            onTouchStart: () => (ignoreBlur.current = true),
            onMouseEnter: () => (ignoreBlur.current = true),
            onMouseLeave: () => (ignoreBlur.current = false),
          },
        )}
    </div>
  );
}

const defaultMenuStyle = {
  borderRadius: "3px",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
  background: "rgba(255, 255, 255, 0.9)",
  padding: "2px 0",
  fontSize: "90%",
  position: "fixed",
  overflow: "auto",
  maxHeight: "50%",
};

const inputStyles = {
  width: "100%",
  fontWeight: "300",
  fontSize: "14px",
  paddingTop: "7px",
  paddingBottom: "7px",
  paddingRight: "32px",
  paddingLeft: "11px",
  lineHeight: "20px",
  borderRadius: "6px",
  borderWidth: "1px",
  borderColor: "#ECECEC",
  borderStyle: "solid",
};

const menuStyles = {
  position: "absolute",
  paddingTop: "10px",
  paddingBottom: "10px",
  zIndex: 150,
  width: "100%",
  fontWeight: "300",
  fontSize: "14px",
  borderRadius: "6px",
  borderWidth: "1px",
  borderColor: "#ECECEC",
  borderStyle: "solid",
  backgroundColor: "#fff",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
};

const itemStyles = {
  width: "100%",
  padding: "10px",
};

const highlightedItemStyles = {
  backgroundColor: "#E5F0FE",
};

const wrapperStyles = {
  display: "block",
  width: "100%",
  position: "relative",
};

Autocomplete.propTypes = {
  items: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  valueProperty: PropTypes.string,
  labelProperty: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  placeholder: PropTypes.string,
  shouldItemRender: PropTypes.func,
  isItemSelectable: PropTypes.func,
  sortItems: PropTypes.func,
  renderItem: PropTypes.func,
  renderMenu: PropTypes.func,
  menuStyle: PropTypes.object,
  renderInput: PropTypes.func,
  inputProps: PropTypes.object,
  wrapperProps: PropTypes.object,
  wrapperStyle: PropTypes.object,
  autoHighlight: PropTypes.bool,
  selectOnBlur: PropTypes.bool,
  onMenuVisibilityChange: PropTypes.func,
  open: PropTypes.bool,
};

const ArrowSVG = () => {
  return (
    <svg
      style={{
        position: "absolute",
        right: "12px",
        top: "7px",
        height: "20px",
        color: "#717273",
        pointerEvents: "none",
      }}
      width="1em"
      height="1em"
      viewBox="0 0 21 32"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      class="rs-picker-caret-icon rs-icon rs-icon"
      aria-label="angle down"
      data-category="legacy"
    >
      <path d="M19.196 13.143a.612.612 0 01-.179.411l-8.321 8.321c-.107.107-.268.179-.411.179s-.304-.071-.411-.179l-8.321-8.321c-.107-.107-.179-.268-.179-.411s.071-.304.179-.411l.893-.893a.582.582 0 01.411-.179c.143 0 .304.071.411.179l7.018 7.018 7.018-7.018c.107-.107.268-.179.411-.179s.304.071.411.179l.893.893a.617.617 0 01.179.411z"></path>
    </svg>
  );
};

export default Autocomplete;



// WEBPACK FOOTER //
// ./src/components/AutoComplete.js